import type { AppLanguage } from '../i18n/types';

export type PlatformTermsSection = {
  title: string;
  body: string;
};

export type PlatformTermsVariant = 'default' | 'assetOwner';

export type PlatformTermsContent = {
  heading: string;
  intro: string;
  sections: PlatformTermsSection[];
};

/** Canonical platform terms — update here for all registration and agreement flows. */
const PLATFORM_TERMS_EN: PlatformTermsContent = {
  heading: 'Terms and Conditions',
  intro:
    'Please read these terms and conditions carefully before using our website and services.',
  sections: [
    {
      title: '1. Acceptance of Terms',
      body: 'By accessing and using this website, you accept and agree to be bound by the terms and provision of this agreement.',
    },
    {
      title: '2. Use License',
      body: 'Permission is granted to temporarily download one copy of the materials on this website for personal, non-commercial transitory viewing only.',
    },
    {
      title: '3. Disclaimer',
      body: 'The materials on this website are provided on an “as is” basis. We make no warranties, expressed or implied, and hereby disclaim and negate all other warranties including, without limitation, implied warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of intellectual property or other violation of rights.',
    },
    {
      title: '4. Limitations',
      body: 'In no event shall we or our suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use the materials on this website.',
    },
    {
      title: '5. Revisions',
      body: 'We may revise these terms of service at any time without notice. By using this website you are agreeing to be bound by the then current version of these terms of service.',
    },
    {
      title: '6. Contact Information',
      body: 'If you have any questions about these Terms and Conditions, please contact us.',
    },
  ],
};

const PLATFORM_TERMS_HE: PlatformTermsContent = {
  heading: 'תנאים והגבלות',
  intro: 'אנא קראו בעיון את התנאים וההגבלות לפני השימוש באתר ובשירותים שלנו.',
  sections: [
    {
      title: '1. קבלת התנאים',
      body: 'בגישה לשימוש באתר זה, אתם מסכימים ומקבלים להיות מחויבים לתנאים ולהוראות של הסכם זה.',
    },
    {
      title: '2. רישיון שימוש',
      body: 'ניתן לשכפל עותק אחד זמני של החומרים באתר זה לצפייה אישית, לא מסחרית וזמנית בלבד.',
    },
    {
      title: '3. הצהרת אחריות',
      body: 'החומרים באתר זה מסופקים “כפי שהם”. איננו מעניקים כל אחריות, מפורשת או משתמעת, ומבטלים כל אחריות אחרת, לרבות, ללא הגבלה, אחריות משתמעת לסחירות, התאמה למטרה מסוימת, או אי-הפרה של קניין רוחני או הפרת זכויות אחרות.',
    },
    {
      title: '4. הגבלות',
      body: 'בשום מקרה לא נהיה אחראים, אנו או ספקינו, לכל נזק (לרבות, ללא הגבלה, נזקים כתוצאה מאובדן נתונים או רווח, או כתוצאה מהפרעה עסקית) הנובעים מהשימוש או מאי-היכולת להשתמש בחומרים באתר זה.',
    },
    {
      title: '5. עדכונים',
      body: 'אנו רשאים לעדכן תנאי שירות אלה בכל עת ללא הודעה מוקדמת. בשימוש באתר אתם מסכימים להיות מחויבים לגרסה העדכנית של תנאי השירות.',
    },
    {
      title: '6. פרטי קשר',
      body: 'אם יש לכם שאלות לגבי תנאים והגבלות אלה, אנא צרו קשר.',
    },
  ],
};

/** Asset-owner / supplier agreement shown on owner registration, profile, and asset create. */
const ASSET_OWNER_TERMS_HE: PlatformTermsContent = {
  heading: 'תנאים והגבלות — בעל נכס',
  intro: 'אנא קראו בעיון את תנאי ההתקשרות עם PLACY לפני פרסום נכס או שימוש בפלטפורמה כספק.',
  sections: [
    {
      title: '1. מהות הפעילות והשירות',
      body:
        'PLACY מפעילה אפליקציה ופלטפורמה דיגיטלית המאגדת מאות משרדים, חללי עבודה משותפים, חדרי ישיבות, חדרי כושר, קליניקות וחדרי הרצאות, ומאפשרת למשתמשי קצה לאתר, להזמין ולשלם עבור שירותי אירוח ושימוש במרחבי עבודה אלו בזמן אמת. PLACY מבצעת פעילות שיווק ופרסום רחבת היקף בכלל פלטפורמות המדיה והדיגיטל, מגייסת לקוחות משלמים וסולקת את התשלום בגין ההזמנות ישירות דרך האפליקציה. לאחר ביצוע התשלום בהצלחה, מוקנית ללקוח הזכות להשתמש בנכס/בשירות שהוזמן.',
    },
    {
      title: '2. התחייבויות בעל הנכס (הספק)',
      body:
        '• התחייבות לזמינות: הספק מתחייב ומצהיר כי תהיה זמינות מלאה ותקינה לנכסים, למשרדים או לחדרי הישיבות המפורסמים מטעמו באפליקציית PLACY, בהתאם למועדים ולימים המוגדרים במערכת.\n' +
        '• מניעת כפל הזמנות: באחריות הספק הבלעדית לוודא כי נכס שהוזמן ושולם עבורו באמצעות האפליקציה יהיה פנוי ומוכן לשימוש הלקוח. במידה ותיווצר אי-זמינות או הזמנה כפולה באשמת הספק, יפצה הספק את PLACY או את הלקוח בגין כל נזק או עלות חלופית שיגרמו.\n' +
        '• רמת שירות: הספק מתחייב להעניק ללקוחות המגיעים דרך האפליקציה שירות מקצועי, אדיב וברמה הגבוהה ביותר, התואמת את תנאי הפרסום.',
    },
    {
      title: '3. מודל מסחרי, עמלות וסליקה',
      body:
        '• סליקת תשלומים: PLACY אחראית על גבייה וסליקת התשלום המלא מהלקוח הסופי בעת ביצוע ההזמנה באפליקציה.\n' +
        '• גובה העמלה: הספק מאשר ומסכים כי PLACY תגבה עמלת שיווק ותיווך בגובה של 30% (שלושים אחוזים) + מע"מ מתוך כל סך עסקה/לקוח משלם שיגיע דרך הפלטפורמה.\n' +
        '• העברת כספים וחשבונות: בסוף כל חודש קלנדרי, PLACY תפיק דוח ריכוז הזמנות וחשבונית בגין עמלתה. יתרת התשלום (70% מסך העסקאות שנשארו לאחר ניכוי העמלה) תועבר לספק כנגד חשבונית מס כדין שתוצא על ידי הספק.\n' +
        '• תנאי תשלום: התשלום יועבר לספק באמצעות העברה בנקאית בסוף כל חודש קלנדרי.',
    },
    {
      title: '4. תקופת ההסכם וסיומו',
      body:
        'הסכם זה הינו ללא הגבלת זמן. יחד עם זאת, לכל אחד מהצדדים תעמוד הזכות המלאה להודיע לצד השני על ביטול ההתקשרות וסיום ההסכם, מכל סיבה שהיא, וזאת באמצעות מתן הודעה בכתב (לרבות בדואר אלקטרוני) של 30 יום מראש. סיום ההסכם לא יפגע בהזמנות קיימות שאושרו ושולמו טרם מועד הסיום, והספק מחויב לכבדן.',
    },
    {
      title: '5. מדיניות ביטולים',
      body:
        '• עד שבוע לפני — החזר מלא\n' +
        '• עד 24 שעות לפני — החזר של 50%\n' +
        '• פחות מ-24 שעות לפני — ללא החזר',
    },
  ],
};

const ASSET_OWNER_TERMS_EN: PlatformTermsContent = {
  heading: 'Terms and Conditions — Asset Owner',
  intro:
    'Please read these PLACY supplier terms carefully before listing an asset or using the platform as a provider.',
  sections: [
    {
      title: '1. Nature of the activity and service',
      body:
        'PLACY operates an application and digital platform that aggregates hundreds of offices, coworking spaces, meeting rooms, gyms, clinics, and lecture halls, and enables end users to find, book, and pay for hospitality services and use of these workspaces in real time. PLACY conducts large-scale marketing and advertising across media and digital platforms, acquires paying customers, and collects payment for bookings directly through the application. After payment is completed successfully, the customer is granted the right to use the booked asset/service.',
    },
    {
      title: '2. Obligations of the asset owner (the supplier)',
      body:
        '• Availability commitment: The supplier undertakes and declares that there will be full and proper availability of the assets, offices, or meeting rooms listed by the supplier in the PLACY application, in accordance with the dates and days defined in the system.\n' +
        '• Preventing double bookings: It is the supplier’s sole responsibility to ensure that an asset booked and paid for through the application is vacant and ready for the customer’s use. If unavailability or a double booking occurs due to the supplier’s fault, the supplier shall compensate PLACY or the customer for any damage or alternative costs incurred.\n' +
        '• Service standard: The supplier undertakes to provide customers arriving through the application with professional, courteous service of the highest standard, consistent with the published listing terms.',
    },
    {
      title: '3. Commercial model, fees, and payment processing',
      body:
        '• Payment processing: PLACY is responsible for collecting and processing the full payment from the end customer when the booking is made in the application.\n' +
        '• Commission amount: The supplier confirms and agrees that PLACY will charge a marketing and brokerage commission of 30% (thirty percent) + VAT of the total of every transaction/paying customer acquired through the platform.\n' +
        '• Fund transfers and invoicing: At the end of each calendar month, PLACY will issue a booking summary report and an invoice for its commission. The remaining payment (70% of the total transactions remaining after deduction of the commission) will be transferred to the supplier against a lawful tax invoice issued by the supplier.\n' +
        '• Payment terms: Payment will be transferred to the supplier by bank transfer at the end of each calendar month.',
    },
    {
      title: '4. Term of the agreement and termination',
      body:
        'This agreement is for an unlimited term. Nevertheless, each party shall have the full right to notify the other party of cancellation of the engagement and termination of the agreement, for any reason whatsoever, by giving written notice (including by email) 30 days in advance. Termination of the agreement shall not affect existing bookings that were approved and paid for before the termination date, and the supplier is obligated to honor them.',
    },
    {
      title: '5. Cancellation policy',
      body:
        '• Up to one week before — full refund\n' +
        '• Up to 24 hours before — 50% refund\n' +
        '• Less than 24 hours before — no refund',
    },
  ],
};

export function getPlatformTermsContent(
  language: AppLanguage,
  variant: PlatformTermsVariant = 'default',
): PlatformTermsContent {
  if (variant === 'assetOwner') {
    return language === 'he' ? ASSET_OWNER_TERMS_HE : ASSET_OWNER_TERMS_EN;
  }
  return language === 'he' ? PLATFORM_TERMS_HE : PLATFORM_TERMS_EN;
}
